import axios from 'axios'
import { Token } from '../types/auth'
import { API_VERSION } from '../hooks/useAxios'
import jwtDecode, { JwtPayload } from 'jwt-decode'
import { Safety } from '../classes/Safety'

/**
 * Window event fired when a dead session is dropped on a page that does not need one.
 * `Initializer` listens to it and clears the in-memory session state.
 */
export const SESSION_EXPIRED_EVENT = 'enion:session-expired'

/**
 * Clean up user specific stuff from local store and go to login screen.
 *
 * On a page that is reachable without a session — login, registration, password
 * reset and the pages opened from a mailed link — stay put: a password-reset link
 * opened in a browser holding a stale `user` with an expired token fires `/auth/me`,
 * gets a 401 and used to bounce to the login page before the set-password form was
 * ever seen. The storage is wiped either way; the page just also has to drop the
 * session it already read into memory, which `SESSION_EXPIRED_EVENT` takes care of.
 */
export function sessionExpired() {
  Safety.clear()
  if (Safety.isUnauthenticated(document.location.pathname)) {
    window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT))
  } else {
    document.location = '/app/login'
  }
}
/**
 * How long a token refresh may take. The first page load of a new build waits for it
 * with nothing rendered (see `adoptSessionForNewBuild()`).
 */
const REFRESH_TIMEOUT_MS = 10000

let refreshPromise: Promise<null|Token> | undefined
/**
 * Try if we have refresh token and can get new token.
 *
 * The refresh token is taken out of storage for the duration of the request. It is
 * put back when the request failed without the server refusing the token (no
 * response, timeout, 5xx), so that a server restarting under a deploy does not cost
 * the session.
 */
export async function trySessionRefresh(): Promise<null|Token> {

  // Share parallel requests.
  if (refreshPromise) {
    return refreshPromise
  }

  const refreshToken = localStorage.getItem('refresh_token')
  if (refreshToken) {
    Safety.set('refresh_token', undefined)
    const payload: JwtPayload = jwtDecode(refreshToken)
    if (payload.exp as number * 1000 < new Date().getTime()) {
      return null
    }
    refreshPromise = axios({
      method: 'POST',
      url: `/api/${API_VERSION}/auth/refresh`,
      timeout: REFRESH_TIMEOUT_MS,
      headers: {
        'Content-Type': 'application/json'
      },
      data: {
        token: refreshToken
      }
    }).then(res => {
      const { refresh_token, token } = res.data
      Safety.set('token', token)
      Safety.set('refresh_token', refresh_token)
      refreshPromise = undefined
      return token as Token
    }).catch(err => {
      refreshPromise = undefined
      console.error(err)
      if (!err.response || err.response.status >= 500) {
        Safety.set('refresh_token', refreshToken)
      }
      return null
    })

    return refreshPromise
  }

  return null
}
